/**
 * Global cart-reservation countdown for the DefaultV2 theme.
 *
 * When a cart is tied to an order slot with a limited validity, the server exposes the slot
 * expiration (ISO-8601 UTC) via `ViewBag.SlotExpirationUtc`, rendered into the config element
 * `#slot-countdown-config`. This component drives two synchronized displays from a single timer:
 * a sticky bar under the navbar (desktop) and a compact badge in the header (mobile).
 *
 * The color shifts with the remaining share of the initial duration (green → orange → red).
 * When the timer reaches zero it stops, hides the displays and shows the cart-expired modal;
 * confirming the modal navigates to the ReturnMap endpoint, which closes the FloatingSale and
 * removes the cart cookies server-side. This mirrors the legacy Default theme's behavior without
 * jQuery/Bootstrap.
 */

// Color class sets per remaining-time state. Each target keeps its own base layout classes;
// only these background/text classes are swapped on every tick.
const STATE_CLASSES = {
  safe: ['bg-green-100', 'text-green-800'],
  warning: ['bg-amber-100', 'text-amber-900'],
  danger: ['bg-red-100', 'text-red-800'],
} as const;

const ALL_STATE_CLASSES = [
  ...STATE_CLASSES.safe,
  ...STATE_CLASSES.warning,
  ...STATE_CLASSES.danger,
];

/** Thresholds (share of initial duration) below which the warning / danger colors kick in. */
const WARNING_RATIO = 0.4;
const DANGER_RATIO = 0.15;

/**
 * Initializes the slot countdown. Safe to call on every page: without a config element or a
 * populated expiration it does nothing and leaves all timer elements hidden.
 */
export function initSlotCountdown(): void {
  const config = document.getElementById('slot-countdown-config');
  if (!config) return;

  const expirationIso = config.dataset.expiration?.trim();
  if (!expirationIso) return;

  const returnMapUrl = config.dataset.returnMapUrl ?? '';
  const countdownEnd = new Date(expirationIso);
  if (Number.isNaN(countdownEnd.getTime())) return;

  const bar = document.getElementById('slot-countdown-bar');
  const badge = document.getElementById('slot-countdown-badge');
  const colorTargets = [bar, badge].filter((el): el is HTMLElement => el !== null);
  const timeTargets = Array.from(
    document.querySelectorAll<HTMLElement>('[data-slot-countdown-time]')
  );
  const modal = document.getElementById('cart-expired-modal');
  const okButton = document.getElementById('cart-expired-ok-btn');

  // Capture the initial remaining span as the 100% reference for the color ratio.
  const totalMs = Math.max(countdownEnd.getTime() - Date.now(), 0);

  let intervalId: number | undefined;

  const setState = (state: keyof typeof STATE_CLASSES): void => {
    colorTargets.forEach((el) => {
      el.classList.remove(...ALL_STATE_CLASSES);
      el.classList.add(...STATE_CLASSES[state]);
    });
  };

  const showDisplays = (): void => {
    colorTargets.forEach((el) => el.classList.remove('hidden'));
  };

  const hideDisplays = (): void => {
    colorTargets.forEach((el) => el.classList.add('hidden'));
  };

  const expire = (): void => {
    timeTargets.forEach((el) => (el.textContent = '00:00'));
    hideDisplays();
    if (intervalId !== undefined) {
      window.clearInterval(intervalId);
      intervalId = undefined;
    }
    if (modal) modal.classList.remove('hidden');
  };

  const tick = (): void => {
    const diffMs = countdownEnd.getTime() - Date.now();
    if (diffMs <= 0) {
      expire();
      return;
    }

    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    const label = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    timeTargets.forEach((el) => (el.textContent = label));

    const ratio = totalMs > 0 ? diffMs / totalMs : 0;
    if (ratio <= DANGER_RATIO) {
      setState('danger');
    } else if (ratio <= WARNING_RATIO) {
      setState('warning');
    } else {
      setState('safe');
    }
  };

  // Confirming the expired modal hands off to the server, which closes the cart and redirects.
  okButton?.addEventListener('click', () => {
    if (returnMapUrl) {
      window.location.href = returnMapUrl;
    } else {
      modal?.classList.add('hidden');
    }
  });

  showDisplays();
  tick();
  intervalId = window.setInterval(tick, 1000);
}
